import { useEffect, useState } from "react";
import { auth, db } from "../firebase/firebase";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { useAuth, useAuthRedirect } from "../context/AuthContext";
import { onAuthStateChanged, User } from "firebase/auth";
import AgentCreatorForm from "../components/AgentCreatorForm";
import { useUserPlan } from "../context/UserPlanContext";
import SessionSidebar from "../components/SessionSidebar";
import PaystackCheckoutOneTime from "../components/PaystackCheckout";
import { Link } from "react-router-dom";

export default function MyLibrary() {
  const [newuser, setNewUser] = useState<User | null>(null);
  const [authUserLoading, setAuthUserLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  useAuthRedirect();

  const { isPro } = useUserPlan();
  const [myAgents, setMyAgents] = useState<any[]>([]);
  const [favoritedAgents, setFavoritedAgents] = useState<any[]>([]);
  const [agentBeingEdited, setAgentBeingEdited] = useState<any | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setNewUser(firebaseUser);
      setAuthUserLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    if (!isPro) return;

    const fetchMyAgents = async () => {
      const userId = user.uid;
      const snap = await getDocs(collection(db, `users/${userId}/agents`));
      const agents = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMyAgents(agents);
    };

    const fetchFavorites = async () => {
      const userId = user.uid;
      const snap = await getDocs(collection(db, `favorites/${userId}/agents`));
      const results = [];
      for (const docRef of snap.docs) {
        const agentId = docRef.id;
        const meta = docRef.data();
        const agentPath = `users/${meta.creatorId}/agents/${agentId}`;
        const agentDoc = await getDoc(doc(db, agentPath));
        if (agentDoc.exists()) {
          results.push({ id: agentId, ...agentDoc.data() });
        }
      }
      setFavoritedAgents(results);
    };

    fetchMyAgents();
    fetchFavorites();
  }, [user, authLoading, isPro]);

  useEffect(() => {
    if (user) {
      // Force token refresh
      user.getIdToken(true).catch((error) => {
        console.error("Failed to refresh token:", error);
      });
    }
  }, [user]);

  const handleDeleteAgent = async (id: string) => {
    if (authLoading || !user) return;

    const userId = user.uid;
    await deleteDoc(doc(db, `users/${userId}/agents/${id}`));
    setMyAgents((prev) => prev.filter((a) => a.id !== id));
  };

  if (authLoading) {
    return <div className="p-6 text-offwhite">Loading...</div>;
  }

  if (!user) {
    return null;
  }

  const userId = user.uid;

  if (!isPro) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center text-white ">
        <div className="mt-24">
          <h2 className="text-2xl font-semibold mb-2"> Pro Feature</h2>
          <p className="mb-4">This section is only available to Pro users.</p>
          <div className="mt-4 block bg-teal hover:bg-teal/80 text-black text-center py-2 rounded-lg transition">
            <PaystackCheckoutOneTime />
          </div>

          <Link
            to="/runtime"
            className=" block mt-10 text-teal hover:underline text-center"
          >
            ← Back to Runtime
          </Link>
          <div className="text-center text-sm text-gray-400 py-8 bg-charcoal">
            *Amount will be converted to ZAR for Paystack payments.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen text-sm overflow-hidden">
      <header className="fixed top-0 left-0 w-full bg-secondary border-b border-darkgray p-4 flex justify-between items-center z-50 md:hidden">
        <button
          aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
          aria-expanded={isSidebarOpen}
          className="p-2 bg-teal text-offwhite rounded-lg hover:bg-teal/80 transition"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          {isSidebarOpen ? "✖" : "☰"}
        </button>
        <h2 className="text-lg font-semibold text-offwhite">My Library</h2>
      </header>
      <div
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-secondary border-r border-darkgray p-6 transform ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:relative md:translate-x-0 md:flex md:h-full transition-transform duration-300 ease-in-out`}
      >
        <SessionSidebar userId={userId} isPro={isPro} />
      </div>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className="flex-1 p-8 max-w-4xl mx-auto space-y-10 text-offwhite pt-20 md:pt-14">
        {/* My Agents */}
        <section>
          <h2 className="text-xl font-semibold mb-4 text-offwhite">
            My Agents
          </h2>
          <div className="space-y-4">
            {myAgents.map((agent) => (
              <div
                key={agent.id}
                className="p-4 border border-darkgray rounded-lg bg-secondary shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="text-lg font-medium text-offwhite">
                  {agent.name}
                </h3>
                <p className="text-sm text-gray-400 mb-2">
                  {agent.prompt.slice(0, 100)}...
                </p>
                <div className="flex gap-4 text-sm">
                  <button
                    onClick={() => setAgentBeingEdited(agent)}
                    className="text-teal hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteAgent(agent.id)}
                    className="text-red-400 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {agentBeingEdited && userId && (
            <div className="mt-6">
              <h3 className="text-md font-semibold mb-2 text-offwhite">
                Edit Agent
              </h3>
              <AgentCreatorForm
                userId={userId}
                existingAgent={agentBeingEdited}
              />
            </div>
          )}
        </section>

        {/* Favorited Agents */}
        <section>
          <h2 className="text-xl font-semibold mb-4 text-offwhite">
            Favorited Agents
          </h2>
          <div className="space-y-4">
            {favoritedAgents.map((agent) => (
              <div
                key={agent.id}
                className="p-4 border border-darkgray rounded-lg bg-secondary shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="text-lg font-medium text-offwhite">
                  {agent.name}
                </h3>
                <p className="text-sm text-gray-400">
                  {agent.prompt.slice(0, 100)}...
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
